import { Injectable, OnDestroy } from '@angular/core';
import { UserRoleSystemModuleDto } from '@hub/generated/models';
import { CurrentUserService } from '@hub/services/CurrentUserService';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TvpPermission } from '@tvPackages/_common/TvpPermission';
import { eSystemModule, eUserPermissionLevel, eUserRole, eTvPckgDepartment, eDepartment } from '@hub/generated/enums';
import { SelectItem } from 'primeng/api';

export function getDepartmentLookUps(roles: eUserRole[]) {
  let departmentLU: SelectItem[] = [];
    if (roles.indexOf(eUserRole.Global_Administrator) >= 0 || roles.indexOf(eUserRole.HUBPackages_Packages_Admin) >= 0 || roles.indexOf(eUserRole.HUBPackages_Financial_Admin) >= 0) {
      return [{ value: 1, label: "TV Scripted" },
        { value: 2, label: "TV Non-Scripted" },
        { value: 3, label: "Digital" }];
    }
    if (roles.indexOf(eUserRole.HUBPackages_TVSFinance) >= 0 ||
      roles.indexOf(eUserRole.HUBPackages_TVSManager) >= 0) {
        departmentLU.push({ value: 1, label: "TV Scripted" });
      }
    if (roles.indexOf(eUserRole.HUBPackages_TVNSFinance) >= 0 ||
      roles.indexOf(eUserRole.HUBPackages_TVNSManager) >= 0) {
      departmentLU.push({ value: 2, label: "TV Non-Scripted" });
    }
    if (roles.indexOf(eUserRole.HUBPackages_DIGFinance) >= 0 ||
      roles.indexOf(eUserRole.HUBPackages_DIGManager) >= 0) {
      departmentLU.push({ value: 3, label: "Digital" });
    }

    return departmentLU;
}

@Injectable()
export class TVPPermissionService implements OnDestroy{
  constructor(
    private currentUserService: CurrentUserService
  ) { }

  private _userRoleSystemModules: UserRoleSystemModuleDto[] = [];
  private _userRoles: eUserRole[] = this.currentUserService.SecurityRoles.getValue();
  private _permission: TvpPermission;
  private _department: eTvPckgDepartment;

  permission$ = this.currentUserService.UserSystemModules.pipe(switchMap((modules: UserRoleSystemModuleDto[]) => {
    if (modules === this._userRoleSystemModules) {
      return of(this._permission);
    }
    this._userRoleSystemModules = modules;

    let permission: TvpPermission = <TvpPermission>{};
    permission.isAuthReadPackage = modules.some(x =>
      x.SystemModuleId === eSystemModule.HUBTvPackages_TvPackages && x.UserPermissionLevelFlag >= eUserPermissionLevel.Read),
    permission.isAuthWritePackage = modules.some(
      x => x.SystemModuleId === eSystemModule.HUBTvPackages_TvPackages && x.UserPermissionLevelFlag >= eUserPermissionLevel.Write),
    permission.isAuthOnlyReadPackage = !permission.isAuthWritePackage && permission.isAuthReadPackage;
    permission.isAuthReadUfBooking = modules.some(x =>
      x.SystemModuleId === eSystemModule.HUBTvPackages_UfBookings && x.UserPermissionLevelFlag >= eUserPermissionLevel.Read),
    permission.isAuthWriteUfBooking = modules.some(x =>
      x.SystemModuleId === eSystemModule.HUBTvPackages_UfBookings && x.UserPermissionLevelFlag >= eUserPermissionLevel.Write),
    permission.isAuthOnlyReadUfBooking = !permission.isAuthWriteUfBooking && permission.isAuthReadUfBooking;
    permission.isAuthReadBeBooking = modules.some(x =>
      x.SystemModuleId === eSystemModule.HUBTvPackages_BeBookings && x.UserPermissionLevelFlag >= eUserPermissionLevel.Read),
    permission.isAuthWriteBeBooking = modules.some(x =>
      x.SystemModuleId === eSystemModule.HUBTvPackages_BeBookings && x.UserPermissionLevelFlag >= eUserPermissionLevel.Write),
    permission.isAuthOnlyReadBeBooking = !permission.isAuthWriteBeBooking && permission.isAuthReadBeBooking;
    permission.isAuthReadCorrespondence = modules.some(x =>
      x.SystemModuleId === eSystemModule.HUBTvPackages_Correspondence && x.UserPermissionLevelFlag >= eUserPermissionLevel.Read),
    permission.isAuthWriteCorrespondence = modules.some(x =>
      x.SystemModuleId === eSystemModule.HUBTvPackages_Correspondence && x.UserPermissionLevelFlag >= eUserPermissionLevel.Write),
    permission.isAuthOnlyReadCorrespondence = !permission.isAuthWriteCorrespondence && permission.isAuthReadCorrespondence;
    permission.isAuthWriteAdmin = modules.some(x => x.SystemModuleId === eSystemModule.HUBTvPackages_Admin
      && x.UserPermissionLevelFlag >= eUserPermissionLevel.Write),

    this._permission = permission;
    return of(permission);
  }));

  getDepartmentLU(): SelectItem[] {
    const roles = this.currentUserService.SecurityRoles.getValue();

    return getDepartmentLookUps(roles);
  }

  hasUserSecurityRole(...roles: number[]): boolean {
    if (roles === null || roles.length == 0) {
      return false;
    }

    return this._userRoles.some(v => roles.indexOf(v) !== -1);
  }

  getPermissionForSystemModuleByDepartment(module: eSystemModule, department: eTvPckgDepartment): eUserPermissionLevel {
    if (this.hasUserSecurityRole(eUserRole.Global_Administrator, eUserRole.HUBPackages_Packages_Admin, eUserRole.HUBPackages_Financial_Admin))
      return eUserPermissionLevel.Write;

    switch(module) {
      case eSystemModule.HUBTvPackages_TvPackages:
        return this.getPackagePermissionLevel(department);
      case eSystemModule.HUBTvPackages_UfBookings:
        return this.getUfBookingPermissionLevel(department);
      case eSystemModule.HUBTvPackages_BeBookings:
        return this.getBeBookingPermissionLevel();
      case eSystemModule.HUBTvPackages_Correspondence:
        return this.getCorrespondencePermissionLevel(department);
      default:
        return eUserPermissionLevel.Not_Set;
    }
  }

  private getPackagePermissionLevel(department: eTvPckgDepartment) {
    const shortName = this.getDepartmentShortName(department)

    if (this.hasUserSecurityRole(eUserRole["HUBPackages_" + shortName + "Manager"], eUserRole["HUBPackages_" + shortName + "Finance"]))
      return eUserPermissionLevel.Write;
    if (this.hasUserSecurityRole(eUserRole["HUBPackages_" + shortName + "Intern"], eUserRole["HUBPackages_Packages_ReadOnly"]))
      return eUserPermissionLevel.Read;
    return eUserPermissionLevel.Not_Set;
  }

  private getUfBookingPermissionLevel(department: eTvPckgDepartment) {
    const shortName = this.getDepartmentShortName(department)

    if (this.hasUserSecurityRole(eUserRole["HUBPackages_" + shortName + "Intern"], eUserRole["HUBPackages_" + shortName + "Finance"]))
      return eUserPermissionLevel.Write;
    if (this.hasUserSecurityRole(eUserRole["HUBPackages_Packages_ReadOnly"]))
      return eUserPermissionLevel.Read;
    return eUserPermissionLevel.Not_Set;
  }

  private getBeBookingPermissionLevel() {
    if (this.hasUserSecurityRole(eUserRole["HUBPackages_Packages_ReadOnly"]))
      return eUserPermissionLevel.Read;
    return eUserPermissionLevel.Not_Set;
  }

  private getCorrespondencePermissionLevel(department: eTvPckgDepartment) {
    const shortName = this.getDepartmentShortName(department)

    if (this.hasUserSecurityRole(eUserRole["HUBPackages_" + shortName + "Intern"], eUserRole["HUBPackages_" + shortName + "Manager"], eUserRole["HUBPackages_" + shortName + "Finance"]))
      return eUserPermissionLevel.Write;
    if (this.hasUserSecurityRole(eUserRole["HUBPackages_Packages_ReadOnly"]))
      return eUserPermissionLevel.Read;
    return eUserPermissionLevel.Not_Set;
  }

  private getDepartmentShortName(department: eTvPckgDepartment) {
    switch(department) {
      case eTvPckgDepartment.Digital: return "DIG";
      case eTvPckgDepartment.TV_Scripted: return "TVS";
      case eTvPckgDepartment.TV_NonScripted: return "TVNS";
    }
  }

  setPermissionByDepartment(department: eTvPckgDepartment) {
    this.permission$ = this.currentUserService.SecurityRoles.pipe(switchMap((roles: eUserRole[]) => {
      if (roles === this._userRoles && this._department === department) {
        return of(this._permission);
      }
      this._userRoles = roles;
      this._department = department;

      let permission: TvpPermission = <TvpPermission>{};
      permission.isAuthReadPackage = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_TvPackages, department)  >= eUserPermissionLevel.Read;
      permission.isAuthWritePackage = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_TvPackages, department)  >= eUserPermissionLevel.Write;
      permission.isAuthOnlyReadPackage = !permission.isAuthWritePackage && permission.isAuthReadPackage;

      permission.isAuthReadUfBooking = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_UfBookings, department)  >= eUserPermissionLevel.Read;
      permission.isAuthWriteUfBooking = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_UfBookings, department)  >= eUserPermissionLevel.Write;
      permission.isAuthOnlyReadUfBooking = !permission.isAuthWriteUfBooking && permission.isAuthReadUfBooking;

      permission.isAuthReadBeBooking = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_BeBookings, department)  >= eUserPermissionLevel.Read;
      permission.isAuthWriteBeBooking = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_BeBookings, department)  >= eUserPermissionLevel.Write;
      permission.isAuthOnlyReadBeBooking = !permission.isAuthWriteBeBooking && permission.isAuthReadBeBooking;

      permission.isAuthReadCorrespondence = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_Correspondence, department)  >= eUserPermissionLevel.Read;
      permission.isAuthWriteCorrespondence = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_Correspondence, department)  >= eUserPermissionLevel.Write;
      permission.isAuthOnlyReadCorrespondence = !permission.isAuthWriteCorrespondence && permission.isAuthReadCorrespondence;

      permission.isAuthWriteAdmin = this.getPermissionForSystemModuleByDepartment(eSystemModule.HUBTvPackages_Admin, department)  >= eUserPermissionLevel.Write;
      permission.isAuthEditPackageAgents = this.hasUserSecurityRole(eUserRole.Global_Administrator, eUserRole.HUBPackages_Packages_Admin);

      this._permission = permission;
      return of(permission);
    }));
  }

  ngOnDestroy() {
  }
}
